/**
 * 교회 물품 대여 예약 (/rental) — 메인 화면 「물품 대여 예약」 버튼에서 들어옴
 *  탭: 예약 현황(로그인 없이) · 예약 신청 · 내 신청
 *  로그인은 /report 와 같은 교회 세션(등록번호 + 담당자 이름·휴대폰)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Paper, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { addBizDays, addDays, isOff, josa, offReason, overdueDays, remainingOn } from '../logic';
import RentalCalendar from '../RentalCalendar';
import RequestForm from '../RequestForm';
import HandoverRecords from '../HandoverRecords';
import {
  CalendarData, ChurchSession, fmtLong, fmtShort, lineText, payDeadline, rentalApi, Reservation, won,
} from '../api';
import { formatPhoneInput } from '../../btcert/common';

const pad = (n: number) => (n < 10 ? '0' : '') + n;
const monthRange = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  const from = `${y}-${pad(m)}-01`;
  const last = new Date(Date.UTC(y, m + 1, 0));
  return { from: addDays(from, -14), to: `${last.getUTCFullYear()}-${pad(last.getUTCMonth() + 1)}-${pad(last.getUTCDate())}` };
};
const STATUS_COLOR: Record<string, 'warning' | 'info' | 'success' | 'default' | 'error'> = {
  입금대기: 'warning', 대여확정: 'info', 대여중: 'success', 반납완료: 'default', 확인필요: 'error', 반려: 'default', 취소: 'default', 반납지연: 'error',
};
export const StatusChip: React.FC<{ status: string; extra?: string }> = ({ status, extra }) => (
  <Chip size="small" color={STATUS_COLOR[status] || 'default'} variant={status === '반납완료' || status === '반려' || status === '취소' ? 'outlined' : 'filled'} label={`${status}${extra || ''}`} />
);

const TABS = ['cal', 'apply', 'my'];

const RentalPortal: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab') || '') ? params.get('tab')! : 'cal';
  const setTab = (t: string) => setParams({ tab: t });
  const [church, setChurch] = useState<ChurchSession | null>(null);
  const [checking, setChecking] = useState(true);
  const [month, setMonth] = useState('');
  const [data, setData] = useState<CalendarData | null>(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState('');
  const [filter, setFilter] = useState('all');
  const [done, setDone] = useState<Reservation | null>(null);

  useEffect(() => { rentalApi.churchMe().then(setChurch).catch(() => setChurch(null)).finally(() => setChecking(false)); }, []);
  const load = useCallback(async (m: string) => {
    try {
      const range = monthRange(m || new Date().toISOString().slice(0, 7));
      const d = await rentalApi.calendar(range.from, addDays(range.to, 120));
      setData(d); setError('');
      if (!m) { setMonth(d.today.slice(0, 7)); setSelected(d.today); }
    } catch (e: any) { setError(e.message); }
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  const logout = async () => { await rentalApi.churchLogout().catch(() => undefined); setChurch(null); setTab('cal'); };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '70vh', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography component="h1" fontSize={{ xs: 24, md: 30 }} fontWeight={800}>물품 대여 예약</Typography>
            <Typography color="text.secondary" fontSize={14} mt={0.5}>
              {data?.items.map((i) => i.name).join(' · ') || '퀴즈마스터 · 게임 매트 · 그랑프리 트랙'} — 날짜별 남은 수량을 확인하고 신청하세요.
            </Typography>
            <Box sx={{ width: 44, height: 3, bgcolor: 'primary.main', borderRadius: 1, mt: 1.5 }} />
          </Box>
          {church && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 2, px: 1.5, py: 0.75 }}>
              <Typography fontSize={13}>{church.churchName} · {church.managerName}</Typography>
              <Button size="small" onClick={logout}>로그아웃</Button>
            </Box>
          )}
        </Box>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setDone(null); }} sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 700 } }} textColor="inherit"
          TabIndicatorProps={{ sx: { bgcolor: 'warning.main' } }}>
          <Tab value="cal" label="예약 현황" />
          <Tab value="apply" label="예약 신청" />
          <Tab value="my" label="내 신청" />
        </Tabs>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!data || checking ? <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
          : tab === 'cal' ? (
            <CalendarPanel data={data} month={month} setMonth={setMonth} selected={selected} setSelected={setSelected} filter={filter} setFilter={setFilter} goApply={() => setTab('apply')} goMy={() => setTab('my')} />
          ) : !church ? <ChurchLogin onLogin={setChurch} />
            : tab === 'apply' ? (done ? <Done r={done} data={data} again={() => setDone(null)} goMy={() => { setDone(null); setTab('my'); }} /> : (
              <RequestForm mode="church" items={data.items} settings={data.settings} reservations={data.reservations} today={data.today} church={church}
                onSubmit={async (body) => { const r = await rentalApi.create(body); setDone(r); load(month); window.scrollTo(0, 0); }} />
            ))
              : <MyPanel data={data} reload={() => load(month)} goApply={() => setTab('apply')} />}
      </Container>
    </Box>
  );
};

// ───────── 예약 현황 ─────────
const CalendarPanel: React.FC<{
  data: CalendarData; month: string; setMonth: (m: string) => void; selected: string; setSelected: (s: string) => void;
  filter: string; setFilter: (f: string) => void; goApply: () => void; goMy: () => void;
}> = ({ data, month, setMonth, selected, setSelected, filter, setFilter, goApply, goMy }) => {
  const holidays = data.settings.holidays || [];
  const dayRes = data.reservations.filter((r) => ['입금대기', '대여확정', '대여중'].includes(r.status) && selected >= r.start && selected <= (r.status === '대여중' && data.today > r.end ? data.today : r.end));
  const off = selected ? offReason(selected, holidays) : null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) 320px' }, gap: 2.5, alignItems: 'start' }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 4, minWidth: 0 }}>
        {data.items.length ? (
          <RentalCalendar month={month} onMonth={setMonth} selected={selected} onSelect={setSelected} items={data.items} reservations={data.reservations}
            holidays={holidays} today={data.today} filter={filter} onFilter={setFilter} />
        ) : <Typography color="text.secondary" py={6} textAlign="center">아직 등록된 대여 물품이 없어요.</Typography>}
      </Paper>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
          <Typography fontWeight={800} mb={1}>{fmtLong(selected)} 남은 수량</Typography>
          {off ? <Typography fontSize={13} color="text.secondary">{off}에는 수령·반납을 하지 않아요.</Typography> : data.items.map((it) => {
            const n = remainingOn({ id: it._id, total: it.total, repair: it.repair }, selected, data.reservations, data.today);
            const cap = it.total - (it.repair || 0);
            return (
              <Box key={it._id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                <span>{it.name}</span><b style={{ color: n <= 0 ? undefined : n < cap ? '#fcd34d' : '#86efac' }}>{n <= 0 ? '마감' : n} / {cap}</b>
              </Box>
            );
          })}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
          <Typography fontWeight={800} mb={1}>이날 예약</Typography>
          {!dayRes.length && <Typography fontSize={13} color="text.secondary">이날 잡힌 예약이 없어요.</Typography>}
          {dayRes.map((r) => (
            <Box key={r._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Box><Typography fontSize={14}>{r.church}</Typography><Typography fontSize={12} color="text.secondary">{lineText(r.lines)} · {fmtShort(r.start)}~{fmtShort(r.end)}</Typography></Box>
              <StatusChip status={overdueDays(r, data.today) ? '반납지연' : r.status} />
            </Box>
          ))}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
          <Typography fontWeight={800} mb={1}>대여 안내</Typography>
          <Typography fontSize={12.5} color="text.secondary" lineHeight={1.8}>
            · 기간: 영업일 {data.settings.bizDays}일 이상 (토요일·주일·공휴일 제외)<br />
            · 수령·반납: 본부 방문 (택배 가능 물품만 택배)<br />
            · 입금: 신청 후 {Math.round(data.settings.payDueHours / 24)}일 이내, 이벤트 계좌<br />
            · 취소: 수령일 {data.settings.cancelDays}일 전까지 100% 환불, 이후 환불 없음
          </Typography>
        </Paper>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button fullWidth variant="outlined" color="inherit" onClick={goMy}>내 신청 조회</Button>
          <Button fullWidth variant="contained" onClick={goApply}>예약 신청하기</Button>
        </Box>
      </Box>
    </Box>
  );
};

// ───────── 접속 ─────────
const ChurchLogin: React.FC<{ onLogin: (c: ChurchSession) => void }> = ({ onLogin }) => {
  const [regNo, setRegNo] = useState('');
  const [info, setInfo] = useState<ChurchSession | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setInfo(null); setError('');
    if (regNo.length !== 4) return;
    rentalApi.churchLookup(regNo).then(setInfo).catch((e) => setError(e.message));
  }, [regNo]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try { onLogin(await rentalApi.churchLogin(regNo, name, phone)); } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Paper component="form" onSubmit={submit} variant="outlined" sx={{ maxWidth: 440, mx: 'auto', p: { xs: 2.5, sm: 4 }, borderRadius: 4 }}>
      <Typography fontSize={20} fontWeight={800} mb={0.5}>교회 등록번호로 접속</Typography>
      <Typography fontSize={13} color="text.secondary" mb={2.5}>수료증 제출(/report)과 같은 계정이에요.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TextField label="교회 등록번호 (4자리)" fullWidth value={regNo} autoFocus inputProps={{ inputMode: 'numeric', maxLength: 4 }}
        onChange={(e) => setRegNo(e.target.value.replace(/\D/g, '').slice(0, 4))} sx={{ mb: 2 }} />
      {info && (
        <Box sx={{ p: 1.75, mb: 2, borderRadius: 2.5, border: 1, borderColor: 'warning.main' }}>
          <Typography fontSize={12} fontWeight={800} color="warning.main">등록교회</Typography>
          <Typography fontSize={18} fontWeight={800}>{info.churchName}</Typography>
          <Typography fontSize={13} color="text.secondary">{[info.region, info.location].filter(Boolean).join(' · ')}</Typography>
        </Box>
      )}
      <TextField label="담당자 이름" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} disabled={!info} />
      <TextField label="담당자 휴대폰" fullWidth value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} placeholder="010-0000-0000"
        inputProps={{ inputMode: 'numeric' }} disabled={!info} helperText="BT 신청 때 입력한 담당자 또는 우리 교회 교사의 휴대폰 번호" />
      <Button type="submit" variant="contained" size="large" fullWidth disabled={!info || !name.trim() || phone.replace(/\D/g, '').length < 10 || busy} sx={{ mt: 2.5, minHeight: 50 }}>
        {busy ? '확인 중…' : '접속하기'}
      </Button>
    </Paper>
  );
};

// ───────── 신청 완료 ─────────
const Done: React.FC<{ r: Reservation; data: CalendarData; again: () => void; goMy: () => void }> = ({ r, data, again, goMy }) => (
  <Paper variant="outlined" sx={{ maxWidth: 640, p: { xs: 2.5, sm: 4 }, borderRadius: 4 }}>
    <Typography fontSize={22} fontWeight={800}>신청했어요</Typography>
    <Alert severity="success" sx={{ mt: 2 }}>예약번호 <b>{r.no}</b> · {lineText(r.lines)} · {fmtShort(r.start)} ~ {fmtShort(r.end)}</Alert>
    <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid rgba(59,130,246,.4)', bgcolor: 'rgba(59,130,246,.1)', lineHeight: 1.8, fontSize: 14 }}>
      <b>이벤트 계좌로 입금해 주세요</b><br />
      {data.settings.account} · {data.settings.holder}<br />
      금액 <b>{won(r.total)}</b> · 입금자명 <b>{r.churchName}</b><br />
      <Typography component="span" fontSize={12.5} color="text.secondary">{payDeadline(r, data.settings).toLocaleString('ko-KR')}까지 입금해야 해요. 본부에서 입금을 확인하면 승인되고 대여가 확정돼요.</Typography>
    </Box>
    <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
      <Button variant="contained" onClick={goMy}>내 신청 보기</Button>
      <Button variant="outlined" color="inherit" onClick={again}>새로 신청하기</Button>
    </Box>
  </Paper>
);

// ───────── 내 신청 ─────────
const MyPanel: React.FC<{ data: CalendarData; reload: () => void; goApply: () => void }> = ({ data, reload, goApply }) => {
  const [list, setList] = useState<Reservation[] | null>(null);
  const [error, setError] = useState('');
  const [ask, setAsk] = useState<{ id: string; kind: 'cancel' | 'extend'; date?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState<Reservation | null>(null);
  const holidays = data.settings.holidays || [];
  const refresh = useCallback(() => rentalApi.my().then(setList).catch((e) => setError(e.message)), []);
  useEffect(() => { refresh(); }, [refresh]);
  const loadHandovers = useMemo(() => (id: string) => rentalApi.churchHandovers(id), []);

  const act = async (fn: () => Promise<any>) => {
    setBusy(true); setError('');
    try { await fn(); setAsk(null); await refresh(); reload(); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  if (!list) return error ? <Alert severity="error">{error}</Alert> : <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  return (
    <Box sx={{ display: 'grid', gap: 1.5, maxWidth: 820 }}>
      {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
      {!list.length && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
          <Typography color="text.secondary" mb={2}>아직 신청한 내역이 없어요.</Typography>
          <Button variant="contained" onClick={goApply}>예약 신청하기</Button>
        </Paper>
      )}
      {list.map((r) => {
        const late = overdueDays(r, data.today);
        const before = Math.round((new Date(`${r.start}T00:00:00`).getTime() - new Date(`${data.today}T00:00:00`).getTime()) / 864e5);
        const full = before >= data.settings.cancelDays;
        const canCancel = r.status === '입금대기' || r.status === '대여확정';
        const canExtend = ['입금대기', '대여확정', '대여중'].includes(r.status);
        const askHere = ask && ask.id === r._id ? ask : null;
        return (
          <Paper key={r._id} variant="outlined" sx={{ p: 2, borderRadius: 3, display: 'grid', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography fontWeight={800}>{r.no} · {lineText(r.lines)}</Typography>
              <StatusChip status={late ? '반납지연' : r.status} extra={late ? ` ${late}일` : ''} />
            </Box>
            <Typography fontSize={13} color="text.secondary">{fmtLong(r.start)} ~ {fmtLong(r.end)} · {r.method} · {won(r.total)}</Typography>
            {r.status === '입금대기' && (
              <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid rgba(59,130,246,.4)', bgcolor: 'rgba(59,130,246,.08)', fontSize: 13, lineHeight: 1.7 }}>
                입금 대기 중 · {data.settings.account} {data.settings.holder} · 입금자명 {r.churchName}<br />
                {Date.now() > payDeadline(r, data.settings).getTime()
                  ? <b style={{ color: '#fca5a5' }}>입금 기한이 지났어요. 본부에 연락해 주세요.</b>
                  : <span>{payDeadline(r, data.settings).toLocaleString('ko-KR')}까지</span>}
              </Box>
            )}
            {r.status === '반려' && <Alert severity="error">반려 사유: {r.rejectReason}{r.refund ? ` · 환불: ${r.refund}` : ''}</Alert>}
            {r.status === '취소' && <Typography fontSize={13} color="text.secondary">취소됨{r.refund ? ` · ${r.refund}` : ''}</Typography>}
            {r.method === '택배' && r.trackOut && <Typography fontSize={13} color="text.secondary">발송 송장 {r.trackOut}</Typography>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {canExtend && <Button size="small" variant="outlined" color="inherit" onClick={() => setAsk({ id: r._id, kind: 'extend', date: addBizDays(r.end, 1, holidays) })}>반납일 늦추기</Button>}
              {canCancel && <Button size="small" variant="outlined" color="inherit" onClick={() => setAsk({ id: r._id, kind: 'cancel' })}>취소</Button>}
              {(r.pickup || r.ret) && <Button size="small" variant="outlined" color="inherit" onClick={() => setCert(r)}>확인서 보기</Button>}
            </Box>
            {askHere?.kind === 'cancel' && (
              <Alert severity={full ? 'warning' : 'error'} action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" color="inherit" disabled={busy} onClick={() => act(() => rentalApi.cancel(r._id))}>취소하기</Button>
                  <Button size="small" color="inherit" onClick={() => setAsk(null)}>그만두기</Button>
                </Box>}>
                {full ? `수령일까지 ${before}일 남아 ${r.paid ? '입금액 전액을 환불해요.' : '아직 입금 전이라 바로 취소돼요.'}`
                  : `수령일까지 ${before}일 남아 ${data.settings.cancelDays}일 전 기한이 지났어요. 취소하면 환불되지 않아요.`}
              </Alert>
            )}
            {askHere?.kind === 'extend' && (
              <Alert severity="info" action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" color="inherit" disabled={busy || !askHere.date || isOff(askHere.date, holidays)} onClick={() => act(() => rentalApi.extend(r._id, askHere.date!))}>변경하기</Button>
                  <Button size="small" color="inherit" onClick={() => setAsk(null)}>그만두기</Button>
                </Box>}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  새 반납일
                  <TextField size="small" type="date" value={askHere.date || ''} inputProps={{ min: addDays(r.end, 1) }}
                    onChange={(e) => setAsk({ ...askHere, date: e.target.value })} />
                  {askHere.date && isOff(askHere.date, holidays) && <span>{josa(offReason(askHere.date, holidays) || '', '이라', '라')} 고를 수 없어요.</span>}
                </Box>
              </Alert>
            )}
          </Paper>
        );
      })}
      <HandoverRecords reservation={cert} load={loadHandovers} onClose={() => setCert(null)} />
    </Box>
  );
};

export default RentalPortal;
